import { useCallback, useRef } from 'react'

import { SelectionEvent } from '@viselect/react'

import { useSelectionStore } from '../../../stores'
import { getGridItemId } from '../utils'

/**
 * Wires viselect's marquee events to the selection store: plain marquee
 * replaces, shift/ctrl/meta adds, and marquees starting on an item are skipped
 * (so native item-drag keeps working). `didRunRef` lets the grid's click
 * handler skip the trailing click of a finished marquee.
 */
export const useMarqueeSelection = () => {
  const didRunRef = useRef(false)
  const snapshotRef = useRef<Set<string>>(new Set())

  const onBeforeStart = useCallback(({ event }: SelectionEvent) => {
    const target = event?.target as Element | null
    if (getGridItemId(target)) return false
    return true
  }, [])

  const onStart = useCallback(({ event }: SelectionEvent) => {
    didRunRef.current = true
    const native = event as MouseEvent | TouchEvent | null
    const additive = Boolean(
      native &&
        'shiftKey' in native &&
        (native.shiftKey || native.ctrlKey || native.metaKey)
    )
    if (additive) {
      snapshotRef.current = new Set(useSelectionStore.getState().selectedIds)
    } else {
      snapshotRef.current = new Set()
      useSelectionStore.getState().clear()
    }
  }, [])

  const onMove = useCallback(({ store }: SelectionEvent) => {
    const next = new Set(snapshotRef.current)
    for (const el of store.selected) {
      const id = getGridItemId(el)
      if (id) next.add(id)
    }
    useSelectionStore.setState({ selectedIds: next })
  }, [])

  return { didRunRef, onBeforeStart, onStart, onMove }
}
